'use client';

import { Link } from '@/i18n/routing';
import { usePublicPathname } from '@/lib/use-public-pathname';
import { localeSwitchPath } from '@/lib/locale-switch';
import { useTranslations } from 'next-intl';
import { NastrojkiCookie } from '@/components/seo/nastrojki-cookie';
import { DhLogo } from '@/components/ui/dh-logo';
import { DH_TOOLS } from '@/lib/dh-tools';
import { DH_CONTACT_EMAIL } from '@/components/seo/schema';
import { localesWithPosts } from '@/lib/posts';
import type { FooterPost } from '@/lib/footer-posts';
import { NashiProekty } from '@/components/layout/nashi-proekty';

/**
 * Подвал DataHunter.
 *
 * Переключатель языка здесь тот же, что на основном сайте: сайт живёт на
 * четырёх языках, и человек должен иметь возможность сменить язык с любой
 * страницы, а не только с главной.
 */
/** Инструменты продукта перечислены в `lib/dh-tools.ts`. */

export function DhFooter({ locale, posts }: { locale: string; posts: FooterPost[] }) {
  const t = useTranslations('dh_layout');
  const tBlog = useTranslations('dh_blog');
  const pathname = usePublicPathname();
  const year = String(new Date().getFullYear());

  /**
   * Колонка разборов только там, где есть что читать.
   *
   * Разбор написан пока один и только по-русски, а подвал общий на
   * все языки: на /en, /es и /sr ссылка вела на страницу с одной
   * строчкой «первые разборы уже готовятся». Человек тратит переход,
   * чтобы узнать, что смотреть нечего.
   *
   * Язык приходит сверху, а не берётся из адреса: подвал
   * клиентский, а путь без префикса у языка по умолчанию неотличим
   * от пути без языка вообще.
   *
   * Список из реестра: появится перевод — колонка вернётся сама.
   */
  const hasPosts = localesWithPosts('datahunter').includes(locale as never);

  return (
    <footer className="border-t border-[var(--color-border)] bg-[var(--color-bg)]">
      <div className="max-w-[1280px] mx-auto px-6 py-12">
        <div className="mb-10 max-w-md">
          <div className="flex items-center gap-2.5 mb-3">
            <DhLogo className="w-6 h-6 text-[var(--color-text)]" />
            <span className="font-bold tracking-tight">DataHunter</span>
          </div>
          <p className="text-sm text-[var(--color-text-muted)] leading-relaxed">{t('tagline')}</p>
        </div>

        <div className={`grid grid-cols-2 gap-8 ${hasPosts ? 'md:grid-cols-4' : 'md:grid-cols-3'}`}>
          <div>
            <h2 className="font-semibold text-sm mb-4">{t('tools')}</h2>
            {/* Все инструменты, а не выборочно: раньше в подвале жили
                три из шести, и остальные выглядели второсортными. */}
            <ul className="space-y-2">
              {DH_TOOLS.map((tool) => (
                <li key={tool.href}>
                  <Link
                    href={tool.href}
                    className="text-sm text-[var(--color-text-muted)] hover:text-[var(--color-accent-400)] transition-colors"
                  >
                    {t(tool.label)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {hasPosts && (
            <div>
              {/* Разборы отдельной колонкой, а не в списке инструментов:
                  это не ещё один инструмент. */}
              <h2 className="font-semibold text-sm mb-4">{tBlog('title')}</h2>

              {/*
                Сами разборы, а не описание раздела.

                Раньше здесь стояла фраза «Как сделать дело без установки
                программ и регистрации» — описание раздела с витрины. В
                подвале она читается как приглашение, но нажать на неё
                нельзя: текст в никуда. Соседняя колонка перечисляет сами
                инструменты, и эта должна перечислять сами разборы.

                Заголовки берутся из текстов статей, как на витрине: из
                словаря они разошлись бы с настоящими, и человек нажимал
                бы на одно, а попадал на другое.
              */}
              <ul className="space-y-2">
                {posts.map((post) => (
                  <li key={post.slug}>
                    <Link
                      href={`/blog/${post.slug}`}
                      className="text-sm text-[var(--color-text-muted)] hover:text-[var(--color-accent-400)] transition-colors"
                    >
                      {post.title}
                    </Link>
                  </li>
                ))}
              </ul>

              {/* Ссылка на весь раздел — под списком, как «ещё»: список
                  показывает свежие, а не все. */}
              <Link
                href="/blog"
                className="mt-3 inline-block text-xs text-[var(--color-text-muted)] hover:text-[var(--color-accent-400)] transition-colors"
              >
                {tBlog('all')}
              </Link>
            </div>
          )}

          <div>
            <h2 className="font-semibold text-sm mb-4">{t('contacts')}</h2>
            <a
              href={`mailto:${DH_CONTACT_EMAIL}`}
              className="text-sm text-[var(--color-text-muted)] hover:text-[var(--color-accent-400)] transition-colors"
            >
              {DH_CONTACT_EMAIL}
            </a>
            {/* Политика конфиденциальности и выбор о счётчиках (16.10.2026). */}
            <ul className="mt-3 space-y-2">
              <li>
                <Link
                  href="/legal/privacy-policy"
                  className="text-sm text-[var(--color-text-muted)] hover:text-[var(--color-accent-400)] transition-colors"
                >
                  {t('privacy')}
                </Link>
              </li>
              <NastrojkiCookie
                kakLi
                className="text-sm text-[var(--color-text-muted)] hover:text-[var(--color-accent-400)] transition-colors"
              />
            </ul>
          </div>

          <div>
            <h2 className="font-semibold text-sm mb-4">{t('language')}</h2>
            <div className="flex flex-wrap gap-2">
              {(['en', 'es', 'sr', 'ru'] as const).map((locale) => (
                <Link
                  key={locale}
                  // Со статьи, не написанной на выбранном языке, уводим в
                  // список разборов: прямая ссылка ведёт на 404. Подвал
                  // общий для всего домена, поэтому одна такая ссылка
                  // размножилась бы разом по всему разделу.
                  href={localeSwitchPath(pathname, locale)}
                  locale={locale}
                  className="px-2 py-1 rounded text-xs text-[var(--color-text-muted)] hover:text-[var(--color-accent-400)] border border-[var(--color-border)] hover:border-[var(--color-accent-500)] transition-colors uppercase"
                >
                  {locale}
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Соседние продукты (перелинковка, 19.10.2026). */}
        <NashiProekty sajt="datahunter" locale={locale} />

        <div className="mt-12 pt-8 border-t border-[var(--color-border)]">
          <p className="text-xs text-[var(--color-text-muted)]">{t('copyright', { year })}</p>
        </div>
      </div>
    </footer>
  );
}
